import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, ToggleButton, Form } from 'react-bootstrap';
import useToken from 'hooks/useToken';

/**
 * Componente para seleccionar la moneda de la orden (MXN o USD)
 * - Mantiene los toggles MXN/USD visibles por defecto
 * - Si el usuario presiona el link "Elegir otra moneda" se muestra un DDL
 *   con monedas obtenidas desde /api/contabilidad/CatalogoSAT/ObtenerCatMonedas
 * - Al seleccionar una moneda en el DDL se notifica mediante onCambiarMoneda
 *   y se muestra la moneda seleccionada en lugar del toggle (con opción para cambiar)
 */
const SelectorMoneda = ({ monedaSeleccionada, onCambiarMoneda }) => {
    const { token } = useToken();
    const [opciones, setOpciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [customSelected, setCustomSelected] = useState(null);

    useEffect(() => {
        // if the current monedaSeleccionada was set externally,
        // set/clear customSelected so the correct control is shown
        if (monedaSeleccionada) {
            if (monedaSeleccionada === 'MXN' || monedaSeleccionada === 'USD') {
                setCustomSelected(null);
            } else {
                setCustomSelected(monedaSeleccionada);
            }
        } else {
            setCustomSelected(null);
        }
    }, [monedaSeleccionada]);

    const loadMonedas = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/contabilidad/CatalogoSAT/ObtenerCatMonedas', {
                method: 'POST',
                headers: { Authorization: token ? 'Bearer ' + token : '' }
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data && data.iResponseType === 1) {
                let parsed = null;
                try {
                    parsed = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles;
                } catch {
                    parsed = null;
                }
                const table2 = parsed && (parsed.table2 || parsed.Table2) ? (parsed.table2 || parsed.Table2) : [];
                setOpciones(Array.isArray(table2) ? table2 : []);
            }
        } catch (err) {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    const handleShowDropdown = () => {
        setShowDropdown(true);
        // load monedas if not loaded
        if (!opciones || opciones.length === 0) loadMonedas();
    };

    const handleSelectMoneda = (val) => {
        // if user selected a core currency, clear customSelected so the left toggles reflect it
        if (!val) {
            setCustomSelected(null);
        } else if (val === 'USD' || val === 'MXN') {
            setCustomSelected(null);
        } else {
            setCustomSelected(val);
        }
        setShowDropdown(false);
        if (typeof onCambiarMoneda === 'function') onCambiarMoneda(val);
    };


    // Render toggles with the third control being either a toggle or an inline DDL
    // If dropdown active, show inline select in place of the third toggle
    return (
        <div className="d-flex align-items-center">
            <ButtonGroup>
                <ToggleButton
                    type="radio"
                    id="radio-usd"
                    variant="outline-primary"
                    name="radio-moneda"
                    value="USD"
                    checked={monedaSeleccionada === 'USD'}
                    onChange={() => onCambiarMoneda('USD')}
                >
                    USD
                </ToggleButton>
                <ToggleButton
                    type="radio"
                    id="radio-mxn"
                    variant="outline-primary"
                    name="radio-moneda"
                    value="MXN"
                    checked={monedaSeleccionada === 'MXN'}
                    onChange={() => onCambiarMoneda('MXN')}
                >
                    MXN
                </ToggleButton>
                {showDropdown ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Form.Select size="sm" value={monedaSeleccionada || ''} onChange={(e) => handleSelectMoneda(e.target.value)} disabled={loading} style={{ minWidth: 140 }}>
                            <option value="">Seleccione</option>
                            {opciones && opciones.length > 0 ? (
                                opciones.map((m, i) => (
                                    <option key={i} value={m.vClaveMoneda || m.vMoneda || m.vClave || m.vCodigo}>
                                        {m.vClaveMoneda || m.vMoneda || m.vClave || m.vCodigo}
                                    </option>
                                ))
                            ) : (
                                <option value={customSelected}>{customSelected || '—'}</option>
                            )}
                        </Form.Select>
                        <button type="button" className="btn btn-link btn-sm" onClick={() => setShowDropdown(false)} style={{ marginLeft: 6 }}>Cancelar</button>
                    </div>
                ) : (
                    <ToggleButton
                        type="radio"
                        id="radio-other"
                        variant="outline-primary"
                        name="radio-moneda"
                        value="OTHER"
                        checked={!!customSelected}
                        onChange={() => handleShowDropdown()}
                        onClick={() => handleShowDropdown()}
                    >
                        {customSelected || 'Otra'}
                        <span style={{ marginLeft: 8, fontSize: '0.75rem', color: (customSelected ? '#fff' : undefined) }}>Cambiar</span>
                    </ToggleButton>
                )}
            </ButtonGroup>
        </div>
    );
};

SelectorMoneda.propTypes = {
    monedaSeleccionada: PropTypes.string.isRequired,
    onCambiarMoneda: PropTypes.func.isRequired
};

export default SelectorMoneda;
